import moment from 'moment';
import type { ITechlandCardMetricsRequestPayload } from './_utils';
import { buildTechlandCardMetricsRequest } from './_utils';

/**
 * 与移动端 StudioCardTrendService/utils#getStudioCardTrendTimeRange 中年份钳制一致
 *（rawStart 落在该公历年时，起点改用锚点；周粒度对锚点取 isoWeek 起点）
 */
export const STUDIO_CARD_TREND_CLAMP_IF_RAW_START_YEAR = 2025;
export const STUDIO_CARD_TREND_CLAMP_START_DATE = '2026-01-01';

/** TechlandMultiMetricTrendCard：接口与横轴统一的 7 刻度起点（与 STUDIO_CARD_TREND_CLAMP_START_DATE 一致） */
export const TECHLAND_SEVEN_TICK_API_START = STUDIO_CARD_TREND_CLAMP_START_DATE;

export type TTrendDateUnit = 'day' | 'week' | 'month';

/**
 * HorBar / card_metrics 趋势窗起点：与移动端 getStudioCardTrendTimeRange 前推规则一致。
 * - 日：end 往前 completeMap.day 天
 * - 周：end 往前 completeMap.week 周（不再 startOf isoWeek，避免与移动端差 1 天）
 * - 月：end 往前 completeMap.month 月（不再 startOf month）
 */
export function computeStudioCardTrendStartMoment(
  mEnd: moment.Moment,
  period: string,
  completeMap: {
    day?: number;
    week?: number;
    month?: number;
  },
): moment.Moment {
  const dayN = completeMap.day ?? 5;
  const weekN = completeMap.week ?? 5;
  const monthN = completeMap.month ?? 5;

  let rawStart: moment.Moment;
  if (period === 'monthly') {
    rawStart = mEnd.clone().subtract(monthN, 'months');
  } else if (period === 'weekly') {
    rawStart = mEnd.clone().subtract(weekN, 'weeks');
  } else {
    rawStart = mEnd.clone().subtract(dayN, 'days');
  }

  let mStart = rawStart;
  const needsYearClamp =
    rawStart.year() === STUDIO_CARD_TREND_CLAMP_IF_RAW_START_YEAR;
  if (needsYearClamp) {
    const clampAnchor = moment(
      STUDIO_CARD_TREND_CLAMP_START_DATE,
      'YYYY-MM-DD',
    );
    mStart =
      period === 'weekly'
        ? clampAnchor.clone().startOf('isoWeek')
        : clampAnchor.clone();
  }
  if (mStart.isAfter(mEnd)) {
    mStart = mEnd.clone();
  }
  return mStart;
}

/** 与 HomeFilter filter.date_type 对齐 */
export function getTrendUnitFromPeriod(
  period: string | undefined,
): TTrendDateUnit {
  if (period === 'weekly') {
    return 'week';
  }
  if (period === 'monthly') {
    return 'month';
  }
  return 'day';
}

/** YT{{dateStr}} 标题：日 D / 周 W / 月 M */
export function getTechlandTrendTitleDateStr(
  dateType: string | undefined,
): string {
  if (dateType === 'weekly') {
    return 'W';
  }
  if (dateType === 'monthly') {
    return 'M';
  }
  return 'D';
}

function inclusivePeriodCountBetween(
  anchor: moment.Moment,
  selected: moment.Moment,
  unit: TTrendDateUnit,
): number {
  if (!selected.isSameOrAfter(anchor, 'day')) {
    return 0;
  }
  if (unit === 'day') {
    return selected.diff(anchor, 'days') + 1;
  }
  if (unit === 'week') {
    const aw = anchor.clone().startOf('isoWeek');
    const sw = selected.clone().startOf('isoWeek');
    return sw.diff(aw, 'weeks') + 1;
  }
  const am = anchor.clone().startOf('month');
  const sm = selected.clone().startOf('month');
  return sm.diff(am, 'months') + 1;
}

function enumerateTechlandTrendCategoryKeys(
  anchor: moment.Moment,
  selected: moment.Moment,
  unit: TTrendDateUnit,
): string[] {
  const keys: string[] = [];
  if (unit === 'day') {
    const cur = anchor.clone().startOf('day');
    const end = selected.clone().startOf('day');
    while (cur.isSameOrBefore(end, 'day')) {
      keys.push(cur.format('YYYY-MM-DD'));
      cur.add(1, 'day');
    }
    return keys;
  }
  if (unit === 'week') {
    let cur = anchor.clone().startOf('isoWeek');
    const end = selected.clone().startOf('isoWeek');
    while (cur.isSameOrBefore(end, 'day')) {
      keys.push(cur.format('YYYY-MM-DD'));
      cur.add(1, 'week');
    }
    return keys;
  }
  let cur = anchor.clone().startOf('month');
  const end = selected.clone().startOf('month');
  while (cur.isSameOrBefore(end, 'month')) {
    keys.push(cur.format('YYYY-MM'));
    cur.add(1, 'month');
  }
  return keys;
}

/** 不足 7 个主刻度时：在枚举末档后再朝后推 2 个同粒度刻度，便于接口 end_time 与横轴右端留白 */
function appendTwoTicksAfterEnumeratedKeys(
  keys: string[],
  unit: TTrendDateUnit,
): string[] {
  if (!keys.length) {
    return keys;
  }
  const out = [...keys];
  const lastKey = String(out[out.length - 1] ?? '').trim();
  if (unit === 'month') {
    const sm = /^(\d{4})-(\d{2})$/.exec(lastKey);
    let cur = sm
      ? moment(`${sm[1]}-${sm[2]}-01`, 'YYYY-MM-DD').startOf('month')
      : moment(lastKey, 'YYYY-MM-DD', true).startOf('month');
    if (!cur.isValid()) {
      return out;
    }
    for (let j = 0; j < 2; j += 1) {
      cur = cur.clone().add(1, 'month');
      out.push(sevenTickFormatKey(cur, unit));
    }
    return out;
  }
  if (unit === 'week') {
    let cur = moment(lastKey, 'YYYY-MM-DD', true).startOf('isoWeek');
    if (!cur.isValid()) {
      return out;
    }
    for (let j = 0; j < 2; j += 1) {
      cur = cur.clone().add(1, 'week');
      out.push(sevenTickFormatKey(cur, unit));
    }
    return out;
  }
  let cur = moment(lastKey, 'YYYY-MM-DD', true).startOf('day');
  if (!cur.isValid()) {
    return out;
  }
  for (let j = 0; j < 2; j += 1) {
    cur = cur.clone().add(1, 'day');
    out.push(sevenTickFormatKey(cur, unit));
  }
  return out;
}

function sevenTickFormatKey(m: moment.Moment, unit: TTrendDateUnit): string {
  if (unit === 'month') {
    return m.clone().startOf('month').format('YYYY-MM');
  }
  if (unit === 'week') {
    return m.clone().startOf('isoWeek').format('YYYY-MM-DD');
  }
  return m.clone().startOf('day').format('YYYY-MM-DD');
}

/**
 * 横轴最后一档类目键对应的接口 end_time（取该日/该 ISO 周末日/该月末日 YYYY-MM-DD）。
 */
export function trendBucketEndYmdFromCategoryKey(
  categoryKey: string,
  unit: TTrendDateUnit,
): string {
  const k = String(categoryKey ?? '').trim();
  if (!k) {
    return TECHLAND_SEVEN_TICK_API_START;
  }
  const strictMonth = /^(\d{4})-(\d{2})$/.exec(k);
  const m = strictMonth
    ? moment(`${strictMonth[1]}-${strictMonth[2]}-01`, 'YYYY-MM-DD')
    : moment(k, 'YYYY-MM-DD', true);
  const base = m.isValid() ? m : moment(k);
  if (!base.isValid()) {
    return k.slice(0, 10);
  }
  if (unit === 'month') {
    return base.clone().endOf('month').format('YYYY-MM-DD');
  }
  if (unit === 'week') {
    return base.clone().endOf('isoWeek').format('YYYY-MM-DD');
  }
  return base.clone().format('YYYY-MM-DD');
}

/**
 * 第 5 个刻度（1-based）= 筛选结束日 → 0-based 下标 4。
 * 第 1 档锚点、第 5 档筛选日固定；第 2～4 档在 [锚点, 筛选] 上按连续时间四等分 (i/4)*D（可分数天/周/月），再按粒度取整键，避免整数拆段出现 3 天与 7 天相邻的大跳变。第 6、7 档以筛选所在桶起点为基准，两段后推使用同一整数步长 step=max(1,floor(post/2))（post=round(D/2)），避免 post/2 为小数时日轴上出现 4 天与 3 天不等距。
 */
const SEVEN_TICK_FILTER_INDEX = 4;

/**
 * 7 点 6 段：筛选结束日在第 5 点；第 2～4 档为锚点→筛选的连续四等分；后推两段以筛选桶起点为基准各加整数 step=max(1,floor(post/2))，日历/周/月粒度上等间距。
 * 起点接口与横轴锚定 TECHLAND_SEVEN_TICK_API_START（2026-01-01）。
 * 当锚点到选中之间的粒度个数不足 7 时，横轴为锚点→选中的实际粒度序列，并在末档后再朝后推 2 个同粒度刻度。
 * start_time 固定为锚点；end_time 为末档类目对应的周期末日，保证周/月粒度拉全量。
 */
export function resolveTechlandSevenTickWindow(
  filterEndYmd: string | undefined,
  period: string | undefined,
): {
  apiStart: string;
  apiEnd: string;
  xCategoryKeys: string[];
} {
  const anchor = moment(TECHLAND_SEVEN_TICK_API_START, 'YYYY-MM-DD').startOf('day');
  const apiStart = TECHLAND_SEVEN_TICK_API_START;
  const unit = getTrendUnitFromPeriod(period);
  if (!filterEndYmd || !moment(filterEndYmd).isValid()) {
    const onlyKey = sevenTickFormatKey(anchor, unit);
    return {
      apiStart,
      apiEnd: trendBucketEndYmdFromCategoryKey(onlyKey, unit),
      xCategoryKeys: [onlyKey],
    };
  }
  const selected = moment(filterEndYmd).startOf('day');
  const selEffective = selected.isBefore(anchor) ? anchor.clone() : selected;

  const inc = inclusivePeriodCountBetween(anchor, selEffective, unit);
  if (inc < 7) {
    const keys = enumerateTechlandTrendCategoryKeys(anchor, selEffective, unit);
    const baseKeys = keys.length ? keys : [sevenTickFormatKey(selEffective, unit)];
    const xCategoryKeys = appendTwoTicksAfterEnumeratedKeys(baseKeys, unit);
    const lastKey = xCategoryKeys[xCategoryKeys.length - 1];
    return {
      apiStart,
      apiEnd: trendBucketEndYmdFromCategoryKey(lastKey, unit),
      xCategoryKeys,
    };
  }

  const buildSevenTickKeys = (
    base: moment.Moment,
    total: number,
    addUnit: 'days' | 'weeks' | 'months',
  ): string[] => {
    const t = Math.floor(Number(total));
    const safeT = Number.isFinite(t) && t > 0 ? t : 1;
    const postTotal = Math.round(safeT / 2);
    /** 两段后推共用整数步长，避免 postTotal 为奇数时 postTotal/2 取整导致两段日历差不等 */
    const postStep = Math.max(1, Math.floor(postTotal / 2));
    const filterBucketStart =
      unit === 'month'
        ? selEffective.clone().startOf('month')
        : unit === 'week'
          ? selEffective.clone().startOf('isoWeek')
          : selEffective.clone().startOf('day');
    const keys: string[] = [];
    for (let i = 0; i < 7; i += 1) {
      if (i === SEVEN_TICK_FILTER_INDEX) {
        keys.push(sevenTickFormatKey(selEffective, unit));
        continue;
      }
      if (i === 5) {
        keys.push(
          sevenTickFormatKey(
            filterBucketStart.clone().add(postStep, addUnit),
            unit,
          ),
        );
        continue;
      }
      if (i === 6) {
        keys.push(
          sevenTickFormatKey(
            filterBucketStart.clone().add(2 * postStep, addUnit),
            unit,
          ),
        );
        continue;
      }
      const offset = (i * safeT) / 4;
      keys.push(
        sevenTickFormatKey(base.clone().add(offset, addUnit), unit),
      );
    }
    return keys;
  };

  if (unit === 'week') {
    const wAnchor = anchor.clone().startOf('isoWeek');
    const wSel = selEffective.clone().startOf('isoWeek');
    const kSel = wSel.diff(wAnchor, 'weeks');
    const xCategoryKeys = buildSevenTickKeys(wAnchor, kSel, 'weeks');
    const lastKey = xCategoryKeys[xCategoryKeys.length - 1];
    return {
      apiStart,
      apiEnd: trendBucketEndYmdFromCategoryKey(lastKey, unit),
      xCategoryKeys,
    };
  }
  if (unit === 'month') {
    const mAnchor = anchor.clone().startOf('month');
    const mSel = selEffective.clone().startOf('month');
    const kSel = mSel.diff(mAnchor, 'months');
    const xCategoryKeys = buildSevenTickKeys(mAnchor, kSel, 'months');
    const lastKey = xCategoryKeys[xCategoryKeys.length - 1];
    return {
      apiStart,
      apiEnd: trendBucketEndYmdFromCategoryKey(lastKey, unit),
      xCategoryKeys,
    };
  }

  const dSel = selEffective.diff(anchor, 'days');
  const xCategoryKeys = buildSevenTickKeys(anchor, dSel, 'days');
  const lastKey = xCategoryKeys[xCategoryKeys.length - 1];
  return {
    apiStart,
    apiEnd: trendBucketEndYmdFromCategoryKey(lastKey, unit),
    xCategoryKeys,
  };
}

/** 将接口返回的 date 归一到与横轴类目相同的 key（周=iso 周一起点，月=月初） */
export function normalizeTechlandTrendListDateKey(
  raw: string | undefined,
  unit: TTrendDateUnit,
): string {
  if (!raw) {
    return '';
  }
  const s = String(raw).trim();
  let ymd = s;
  if (/^\d{8}$/.test(s)) {
    ymd = `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
  } else if (/^\d{6}$/.test(s)) {
    ymd = `${s.slice(0, 4)}-${s.slice(4, 6)}-01`;
  }
  const m = moment(ymd);
  if (!m.isValid()) {
    return '';
  }
  return sevenTickFormatKey(m, unit);
}

export function applyTrendTimeAdjustments(
  payload: ITechlandCardMetricsRequestPayload,
  opts: {
    startTimeOffset?: number;
    endTimeOffset?: number;
    trendStartTimeFloor?: string | null;
    unit: TTrendDateUnit;
    sevenTick2026Mode?: boolean;
    /** 与 payload 同源：筛选结束日 YYYY-MM-DD */
    filterEndYmd?: string;
    period?: string;
  },
): ITechlandCardMetricsRequestPayload {
  const {
    sevenTick2026Mode,
    filterEndYmd,
    period,
    startTimeOffset,
    endTimeOffset,
    trendStartTimeFloor,
    unit,
  } = opts;

  if (sevenTick2026Mode) {
    const endSrc = filterEndYmd ?? payload.end_time;
    const { apiStart, apiEnd } = resolveTechlandSevenTickWindow(
      typeof endSrc === 'string' ? endSrc : undefined,
      period ?? payload.period,
    );
    return { ...payload, start_time: apiStart, end_time: apiEnd };
  }

  let { start_time: st, end_time: et } = payload;
  let mUnit: 'days' | 'weeks' | 'months' = 'months';
  if (unit === 'day') {
    mUnit = 'days';
  } else if (unit === 'week') {
    mUnit = 'weeks';
  }

  if (typeof startTimeOffset === 'number' && st && moment(st).isValid()) {
    st = moment(st).subtract(startTimeOffset, mUnit).format('YYYY-MM-DD');
  }
  if (typeof endTimeOffset === 'number' && et && moment(et).isValid()) {
    et = moment(et).add(endTimeOffset, mUnit).format('YYYY-MM-DD');
  }
  const floorStr =
    trendStartTimeFloor === null
      ? undefined
      : trendStartTimeFloor ?? undefined;
  if (floorStr && st && moment(st).isValid()) {
    const floorD = moment(floorStr).startOf('day');
    if (floorD.isValid() && moment(st).isBefore(floorD)) {
      st = floorD.format('YYYY-MM-DD');
    }
  }
  return { ...payload, start_time: st, end_time: et };
}

/**
 * 构建 trend_metrics 请求体（与 card_metrics 同结构，仅 metrics 与接口不同）。
 */
export function buildTechlandTrendRequest(
  data: {
    filterVal?: any;
    filterInitData?: any;
  },
  metrics: string[],
): ITechlandCardMetricsRequestPayload {
  return buildTechlandCardMetricsRequest(data, metrics);
}

/**
 * 趋势点是否整段落在筛选器结束日之后（主指标在该点不画折线）。
 * - 日：类目日 > end
 * - 周：ISO 周一起点 > end（该周与 end 日无交集）
 * - 月：自然月初所在月 > end 所在月
 */
export function isTrendPointAfterFilterEnd(
  dateStr: string,
  unit: TTrendDateUnit,
  filterEndYmd: string,
): boolean {
  const end = String(filterEndYmd ?? '').trim().slice(0, 10);
  const mEnd = moment(end, 'YYYY-MM-DD', true);
  if (!mEnd.isValid() || !dateStr) {
    return false;
  }
  if (unit === 'month') {
    const ds = String(dateStr).trim();
    const ymOnly = /^(\d{4})-(\d{2})$/.exec(ds);
    const mMonth = ymOnly
      ? moment(`${ymOnly[1]}-${ymOnly[2]}-01`, 'YYYY-MM-DD')
      : moment(ds, 'YYYY-MM-DD', true).clone().startOf('month');
    /** 月接口常为 YYYYMMDD（如 20260101），format 后为 YYYY-MM-DD，须按自然月比较，不能仅用 YYYY-MM 正则 */
    return mMonth.isValid() && mMonth.isAfter(mEnd, 'month');
  }
  const d = moment(dateStr, 'YYYY-MM-DD', true);
  if (!d.isValid()) {
    return false;
  }
  if (unit === 'week') {
    return d.clone().startOf('isoWeek').isAfter(mEnd, 'day');
  }
  return d.isAfter(mEnd, 'day');
}

export type IFormatTechlandTrendMetricsOpts = {
  /** 筛选器 end_time 日历日 YYYY-MM-DD */
  filterEndYmd?: string;
  period?: string;
  /** 主指标：晚于 filterEndYmd 的点将 value 置为 '' */
  primaryMetricKeys?: string[];
};

/** 趋势接口：日期归一化；可选将主指标在筛选结束日之后的 value 置空，避免折线画出「未来」段 */
export function formatTechlandTrendMetricsResponse(
  data: any,
  opts?: IFormatTechlandTrendMetricsOpts,
): { list: any[]; } {
  const rawList = Array.isArray(data) ? data : data?.list;
  const arr = Array.isArray(rawList) ? rawList : [];
  const fe = String(opts?.filterEndYmd ?? '').trim().slice(0, 10);
  const unit = getTrendUnitFromPeriod(opts?.period);
  const blankKeys = (opts?.primaryMetricKeys ?? []).map(k => String(k).trim()).filter(Boolean);

  const list = arr.map((item: any) => {
    const raw = item?.date ?? '';
    const dateStr = (() => {
      if (!raw) {
        return '';
      }
      const s = String(raw).trim();
      let ymd = s;
      if (/^\d{8}$/.test(s)) {
        ymd = `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
      } else if (/^\d{6}$/.test(s)) {
        return `${s.slice(0, 4)}-${s.slice(4, 6)}`;
      }
      return ymd;
    })();
    const metrics = { ...(item?.metrics ?? {}) };
    if (
      fe &&
      blankKeys.length > 0 &&
      dateStr &&
      isTrendPointAfterFilterEnd(dateStr, unit, fe)
    ) {
      for (const mk of blankKeys) {
        const cell = metrics[mk];
        if (cell && typeof cell === 'object' && 'value' in cell) {
          metrics[mk] = { ...cell, value: '' };
        }
      }
    }
    return { date: dateStr, metrics };
  });
  return { list };
}

/** 副标题用：筛选器结束日 */
export function getTechlandFilterEndForSubtitle(
  filterVal: Record<string, any> | undefined,
): string {
  if (!filterVal?.end_time) {
    return '';
  }
  return String(filterVal.end_time).trim().slice(0, 10);
}

export function formatTechlandAsOfDisplay(
  ymd: string,
  isEn: boolean,
  fmt?: string,
): string {
  if (!ymd) {
    return '';
  }
  const trimmed = String(ymd).trim();
  const cal = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  const d = cal
    ? moment(new Date(Number(cal[1]), Number(cal[2]) - 1, Number(cal[3])))
    : moment(trimmed);
  if (!d.isValid()) {
    return ymd;
  }
  if (fmt) {
    return d.clone().locale(isEn ? 'en' : 'zh-cn').format(fmt);
  }
  return isEn ? d.format('D MMMM') : d.format('M月D日');
}
